import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, SVGOverlay } from 'react-leaflet';
import { createPathComponent } from '@react-leaflet/core';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import NodeMarkerOffline from '@page/Maps/components/icon/gswm-marker-offline.svg';
import NodeMarkerAlarm from '@page/Maps/components/icon/gswm-marker-alarm.svg';
import { Button, Spin, Typography } from 'antd';
import { STREET_BASEMAP } from '@components/map/basemaps';

// Basemap shared with the Maps page (see components/map/basemaps.js for why not OSM/CARTO).
// Dark mode darkens the tiles via CSS (.dash-map in DashboardV2/index.css).
import { ReactComponent as LocationSvg } from '@page/Maps/components/icon/Location.svg';
import {
  RightOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import _ from 'lodash';
import { formatCurrency } from '@utils/utils';

// ─── Marker Cluster Group (react-leaflet v4 compatible) ───────────────────────
const createClusterGroup = (props, context) => {
  const { children: _c, ...options } = props;
  const clusterGroup = new L.MarkerClusterGroup(options);
  return { instance: clusterGroup, context: { ...context, layerContainer: clusterGroup } };
};
const MarkerClusterGroup = createPathComponent(createClusterGroup);

const clusterIconCreate = (cluster) => {
  const count = cluster.getChildCount();
  let color, size;
  if (count < 10) {
    color = '#10b981';
    size = 36;
  } else if (count < 50) {
    color = '#f59e0b';
    size = 42;
  } else {
    color = '#0284c7';
    size = 50;
  }
  return L.divIcon({
    html: `<div style="
      width:${size}px;height:${size}px;
      background:${color};
      border:3px solid #fff;
      border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      box-shadow:0 2px 8px rgba(0,0,0,0.35);
      font-weight:700;font-size:${count >= 100 ? 11 : 13}px;color:#fff;
    ">${count}</div>`,
    className: '',
    iconSize: L.point(size, size),
    iconAnchor: L.point(size / 2, size / 2)
  });
};
// ──────────────────────────────────────────────────────────────────────────────
import { apiGetDevicesForMap } from '@services/ManageDevices/DeviceServices';
import { showError } from '@components/notification';

const DashboardMap = () => {
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [currentZoom, setCurrentZoom] = useState(12);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await apiGetDevicesForMap();
      const convertData = res?.data?.data?.length
        ? res?.data?.data.map((device) => ({
            id: device?.id || '',
            device_id: device?.id || '',
            name: device?.customer_name || '',
            code: device?.customer_id || '',
            address: device?.address || '',
            lat: device?.latitude || '',
            lng: device?.longitude || '',
            serial_no: device?.serial_no,
            model: device?.model,
            meter_reading: device?.meter_reading,
            status: device?.status,
            last_seen_at: device?.last_seen_at,
            rsrp: device?.rsrp,
            snr: device?.snr,
            battery_capacity: device?.battery_capacity,
            alarm_status: device?.alarm_status
          }))
        : [];
      setDevices(convertData);
    } catch (e) {
      console.error('Error fetching map data:', e);
      showError('Không thể tải dữ liệu bản đồ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const createDynamicIcon = (zoom, marker) => {
    const baseSize = 40;
    const scaleFactor = Math.max(0.5, Math.min(2, zoom / 10));
    const size = baseSize * scaleFactor;

    // Ưu tiên hiển thị icon alarm, sau đó offline
    let iconUrl = !_.isEmpty(marker?.alarm_status) ? NodeMarkerAlarm : NodeMarkerOffline;

    return new L.Icon({
      iconUrl: iconUrl,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -size]
    });
  };

  const MapEvents = () => {
    const map = useMapEvents({
      zoomend: () => {
        setCurrentZoom(map.getZoom());
      }
    });
    return null;
  };

  const handleDirectionsClick = (lat, lng) => {
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(googleMapsUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="dash-map-loading" role="status" aria-live="polite">
        <Spin size="large" />
        <span>Đang tải dữ liệu bản đồ...</span>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '400px',
        overflow: 'hidden'
      }}>
      <MapContainer
        center={[20.854646993671487, 106.68337843839323]}
        zoom={12}
        className="dash-map"
        style={{ width: '100%', height: '100%', borderRadius: 8 }}>
        <TileLayer
          attribution={STREET_BASEMAP.attribution}
          url={STREET_BASEMAP.url}
          maxNativeZoom={STREET_BASEMAP.maxNativeZoom}
          maxZoom={STREET_BASEMAP.maxZoom}
        />
        <MapEvents />
        <MarkerClusterGroup
          iconCreateFunction={clusterIconCreate}
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={false}
          zoomToBoundsOnClick={true}
          maxClusterRadius={60}
          spiderfyDistanceMultiplier={2}
          chunkedLoading={true}>
          {devices?.map((marker) => (
            <Marker
              position={[marker.lat, marker.lng]}
              key={marker.id}
              icon={createDynamicIcon(currentZoom, marker)}>
              <Popup>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    minWidth: '250px'
                  }}>
                  <Typography.Text style={{ fontSize: '16px', fontWeight: '500' }}>
                    {marker?.code} - {marker?.name}
                  </Typography.Text>
                  {/* Online/Offline Status */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {marker?.status === 'online' ? (
                      <CheckCircleOutlined style={{ fontSize: '20px', color: '#10b981' }} />
                    ) : (
                      <CloseCircleOutlined style={{ fontSize: '20px', color: '#e11d48' }} />
                    )}
                    <Typography.Text
                      style={{
                        fontSize: '14px',
                        color: marker?.status === 'online' ? '#10b981' : '#e11d48'
                      }}>
                      {marker?.status === 'online'
                        ? 'Online'
                        : marker?.status === 'offline'
                        ? 'Offline'
                        : 'Chưa lắp đặt'}
                    </Typography.Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <LocationSvg style={{ marginRight: '4px' }} />
                    <Typography.Text style={{ fontSize: '14px', color: 'var(--muted)' }}>
                      {`${
                        marker?.address?.length > 35
                          ? `${marker?.address?.slice(0, 35)}...`
                          : marker?.address
                      }`}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text style={{ fontSize: '14px' }}>
                      Số Seri/Model: {marker?.serial_no ?? '-'} / {marker?.model ?? '-'}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text style={{ fontSize: '14px' }}>
                      Chỉ số:{' '}
                      {marker?.meter_reading ? formatCurrency(marker?.meter_reading, 3) : '-'}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text style={{ fontSize: '14px' }}>
                      Lần truyền cuối:{' '}
                      {marker?.last_seen_at
                        ? new Intl.DateTimeFormat('vi-VN', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          }).format(marker.last_seen_at)
                        : '-'}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text style={{ fontSize: '14px' }}>
                      Cường độ sóng (dBm):{' '}
                      {marker?.rsrp ? (
                        <span
                          style={{
                            color:
                              marker.rsrp >= -100
                                ? 'green'
                                : marker.rsrp >= -110
                                ? '#f59e0b'
                                : 'red',
                            fontWeight: '500'
                          }}>
                          {marker.rsrp}
                        </span>
                      ) : (
                        '-'
                      )}
                    </Typography.Text>
                  </div>
                  {/* <div>
                  <Typography.Text style={{ fontSize: '14px' }}>
                    Dung lượng pin (%): {marker?.battery_capacity ?? '-'}
                  </Typography.Text>
                </div> */}
                  <div>
                    <Typography.Text style={{ fontSize: '14px' }}>
                      Cảnh báo:{' '}
                      {_.isEmpty(marker?.alarm_status) ? 'Không có' : marker?.alarm_status}
                    </Typography.Text>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '8px',
                      width: '100%',
                      justifyContent: 'center'
                    }}>
                    <Button
                      type="primary"
                      size="small"
                      icon={<InfoCircleOutlined />}
                      style={{
                        backgroundColor: '#0284c7',
                        color: '#fff',
                        display: 'flex',
                        justifyContent: 'center'
                      }}
                      onClick={() => window.open(`/device/detail/${marker.device_id}`, '_blank')}>
                      Chi tiết
                    </Button>
                    <Button
                      type="primary"
                      size="small"
                      icon={<RightOutlined />}
                      style={{
                        backgroundColor: '#1A73E8',
                        color: '#fff',
                        display: 'flex',
                        justifyContent: 'center'
                      }}
                      onClick={() => handleDirectionsClick(marker.lat, marker.lng)}>
                      Chỉ đường
                    </Button>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
        <SVGOverlay
          attributes={{ stroke: 'none' }}
          bounds={[
            [18.3, 110.1],
            [15.2, 113.7]
          ]}>
          <ellipse
            cx="100%"
            cy="100%"
            rx="100%"
            ry="100%"
            fill="#aad3df"
            fillOpacity="1"
            stroke="none"
          />
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#222"
            stroke="#fff"
            strokeWidth="2"
            paintOrder="stroke"
            style={{
              fontSize: `${Math.max(10, Math.min(30, currentZoom * 1.5))}px`,
              fontWeight: '700',
              fontFamily: 'Arial, sans-serif',
              letterSpacing: '0.5px'
            }}>
            Hoàng Sa (Việt Nam)
          </text>
        </SVGOverlay>
        <SVGOverlay
          attributes={{ stroke: 'none' }}
          bounds={[
            [10.3, 110.7],
            [8.3, 113.7]
          ]}>
          <ellipse
            cx="100%"
            cy="100%"
            rx="100%"
            ry="100%"
            fill="#aad3df"
            fillOpacity="1"
            stroke="none"
          />
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#222"
            stroke="#fff"
            strokeWidth="2"
            paintOrder="stroke"
            style={{
              fontSize: `${Math.max(10, Math.min(30, currentZoom * 1.5))}px`,
              fontWeight: '700',
              fontFamily: 'Arial, sans-serif',
              letterSpacing: '0.5px'
            }}>
            Trường Sa (Việt Nam)
          </text>
        </SVGOverlay>
      </MapContainer>
    </div>
  );
};

export default DashboardMap;
